import {
  BaseDBService,
  BaseDBServiceOpts,
  Context,
  CoreDict,
  Id,
  PaginatedResult,
  createObj,
  enumGetValues,
  runIfDefined,
  getUpdatedString,
  throwAccessDenied,
  throwInvalidParameter,
  throwObjectNotFound,
  trCore,
  validateOptionalPhone,
  validateOptionalString,
  validateRequiredId,
  validateRequiredString,
} from '../../../common'
import {
  AddSalesPointOpts,
  SalesPoint,
  SalesPointFindOptions,
  SalesPointsRepository,
  SalesPointsService,
  SalesPointType,
  UpdateSalesPointOpts,
} from './salesPoints'
import { ROTransaction, RWTransaction, runWithROTransaction } from '../../../db'
import { Permissions } from '../../authorization'
import { CreateEventMode, ModelEvent, ModelEventCreator, ModelEventsProcessor } from '../../events'
import { MunicipalEntitiesRepository } from '../../municipalEntity'
import { UsersRepository } from '../../users'
import { FullTextSearchService } from '../../fullTextSearch'
import { isEqual } from 'lodash'

enum EventType {
  SALES_POINT_ADD = 'SALES_POINT_ADD',
  SALES_POINT_UPDATE = 'SALES_POINT_UPDATE',
  SALES_POINT_DELETE = 'SALES_POINT_DELETE',
  SALES_POINT_MERGE = 'SALES_POINT_MERGE',
}

type AddSalesPointPayload = {
  name?: string
  inn?: string
  type?: SalesPointType
  maxPopulation?: number
  notificationMark?: string
  municipalEntityId: Id
  locality: string
  street?: string
  building?: string
  comment?: string
  area: number
  openingHours?: string
  phone?: string
  responsiblePersonName?: string
}

type UpdateSalesPointPayload = {
  name?: string
  inn?: string
  type?: SalesPointType
  maxPopulation?: number
  notificationMark?: string
  municipalEntityId?: Id
  locality?: string
  street?: string
  building?: string
  comment?: string
  area?: number
  openingHours?: string
  phone?: string
  responsiblePersonName?: string
}

type MergeSalesPointPayload = {
  name?: string
  inn?: string
  type?: SalesPointType
  maxPopulation?: number
  notificationMark?: string
  area: number
  building?: string
  comment?: string
  createdAt: string
  deletedAt?: string
  locality: string
  municipalEntityId: Id
  openingHours?: string
  phone?: string
  street?: string
}

interface DBSalesPointsServiceOpts extends BaseDBServiceOpts<CoreDict> {
  usersRepository: UsersRepository
  salesPointsRepository: SalesPointsRepository
  municipalEntitiesRepository: MunicipalEntitiesRepository
}

export class DBSalesPointsService
  extends BaseDBService<DBSalesPointsServiceOpts, CoreDict>
  implements SalesPointsService
{
  private validatePhone = (phone?: string) => validateOptionalPhone('<b694178a> Телефон', phone?.trim())
  private validateUserID = (userID?: string) => validateRequiredId('<b2f5ff43> User ID', userID)
  private validateNewLocality = (locality?: string) => validateRequiredString('<1f4cd1fc> Locality', locality?.trim())
  private validateName = (name?: string) =>
    validateRequiredString('<cc9c0320> Наименование хозяйствующего субъекта', name)
  private validateInn = (inn?: string) => {
    let validationInn = validateRequiredString('<60c2325d> ИНН', inn)
    validationInn = validateOptionalString('<bb5e3895> ИНН', inn, { maxSize: 12, minSize: 10 }) as string
    return validationInn
  }
  private validateNotificationMark = (notificationMark?: string) =>
    validateRequiredString('<1afafc19> Отметка об уведомлении', notificationMark)
  private validateUpdateLocality = (locality?: string) =>
    validateOptionalString('<e9f223f9> Locality', locality?.trim(), { minSize: 1 })

  constructor(opts: DBSalesPointsServiceOpts) {
    super('DBSalesPointsService', opts)
    this.logger.debug({}, '<3c8f075e> Initialized')
  }

  async findAll(ctx: Context, opts?: SalesPointFindOptions): Promise<PaginatedResult<SalesPoint>> {
    await runWithROTransaction(this.opts.dbConnection, async tx => {
      if (!(await this.opts.permissionsService.hasPermission(ctx, Permissions.SALES_POINTS_VIEW))) {
        await this.opts.permissionsService.checkPermission(ctx, Permissions.ME_SALES_POINTS_VIEW)

        if (!opts?.filter?.municipalEntityId) {
          const user = await this.opts.usersRepository.findById(tx, this.validateUserID(ctx.userId))
          opts = { ...opts, filter: { ...opts?.filter, municipalEntityId: user?.municipalEntityId } }
        }
        await this.checkAccessToMunicipalEntity('<621f9e22>', ctx, tx, [opts?.filter?.municipalEntityId])
      }
    })

    return await runWithROTransaction(this.opts.dbConnection, tx => this.opts.salesPointsRepository.findAll(tx, opts))
  }

  async findById(ctx: Context, id: string): Promise<SalesPoint | undefined> {
    return await runWithROTransaction(this.opts.dbConnection, async tx => {
      const sp = await this.opts.salesPointsRepository.findById(tx, id)
      if (!(await this.opts.permissionsService.hasPermission(ctx, Permissions.SALES_POINTS_VIEW))) {
        await this.opts.permissionsService.checkPermission(ctx, Permissions.ME_SALES_POINTS_VIEW)

        await this.checkAccessToMunicipalEntity('<0d5e4f66>', ctx, tx, [sp?.municipalEntityId])
      }
      return sp
    })
  }

  async findByIdNonDeleted(ctx: Context, id: string): Promise<SalesPoint | undefined> {
    return await runWithROTransaction(this.opts.dbConnection, async tx => {
      const sp = await this.opts.salesPointsRepository.findByIdNonDeleted(tx, id)
      if (!(await this.opts.permissionsService.hasPermission(ctx, Permissions.SALES_POINTS_VIEW))) {
        await this.opts.permissionsService.checkPermission(ctx, Permissions.ME_SALES_POINTS_VIEW)

        await this.checkAccessToMunicipalEntity('<8d299b6e>', ctx, tx, [sp?.municipalEntityId])
      }
      return sp
    })
  }

  async add(ctx: Context, opts: AddSalesPointOpts): Promise<Id> {
    await runWithROTransaction(this.opts.dbConnection, async tx => {
      if (!(await this.opts.permissionsService.hasPermission(ctx, Permissions.SALES_POINTS_MANAGE))) {
        await this.opts.permissionsService.checkPermission(ctx, Permissions.ME_SALES_POINTS_MANAGE)

        await this.checkAccessToMunicipalEntity('<7bba644e>', ctx, tx, [opts.municipalEntityId])
      }
    })

    const event = await runWithROTransaction(this.opts.dbConnection, async tx => {
      if (opts.municipalEntityId) {
        ;(await this.opts.municipalEntitiesRepository.findByIdNonDeleted(tx, opts.municipalEntityId)) ??
          throwObjectNotFound(
            trCore(d => d.salesPoints.municipalEntityNotFoundError, opts.municipalEntityId),
            { uid: '<3ac6513a>' },
          )
      }

      this.validateAddress(opts.street?.trim(), opts.building?.trim(), opts.comment?.trim())

      const entityId = this.opts.idGenerator.generate()

      return await this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.NEW,
        entityId: entityId,
        type: EventType.SALES_POINT_ADD,
        municipalEntityId: opts.municipalEntityId,
        payload: createObj<AddSalesPointPayload>({
          name: this.validateName(opts.name),
          inn: this.validateInn(opts.inn),
          type: opts.type,
          maxPopulation: opts.maxPopulation,
          notificationMark: this.validateNotificationMark(opts.notificationMark),
          municipalEntityId: opts.municipalEntityId,
          locality: this.validateNewLocality(opts.locality),
          street: opts.street,
          building: opts.building,
          comment: opts.comment,
          area: opts.area,
          openingHours: opts.openingHours,
          phone: this.validatePhone(opts.phone),
          responsiblePersonName: opts.responsiblePersonName,
        }),
      })
    })

    await this.opts.eventDispatcher.processEvents(ctx, [event])

    return event.entityId
  }

  async update(ctx: Context, id: string, opts: UpdateSalesPointOpts): Promise<void> {
    const event = await runWithROTransaction(this.opts.dbConnection, async tx => {
      if (!(await this.opts.permissionsService.hasPermission(ctx, Permissions.SALES_POINTS_MANAGE))) {
        await this.opts.permissionsService.checkPermission(ctx, Permissions.ME_SALES_POINTS_MANAGE)

        const sp = await this.opts.salesPointsRepository.findById(tx, id)

        await this.checkAccessToMunicipalEntity('<1327d013>', ctx, tx, [opts.municipalEntityId, sp?.municipalEntityId])
      }

      const sp = await this.checkForSalesPointExists('<0566522e>', ctx, tx, id)

      if (opts.municipalEntityId) {
        ;(await this.opts.municipalEntitiesRepository.findByIdNonDeleted(tx, opts.municipalEntityId)) ??
          throwObjectNotFound(
            trCore(d => d.salesPoints.municipalEntityNotFoundError, opts.municipalEntityId),
            { uid: '<c30eba43>' },
          )
      }
      this.validateAddress(
        opts.street?.trim() ?? sp.street,
        opts.building?.trim() ?? sp.building,
        opts.comment?.trim() ?? sp.comment,
      )

      return await this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.UPDATE,
        entityId: id,
        type: EventType.SALES_POINT_UPDATE,
        municipalEntityId: sp.municipalEntityId,
        payload: createObj<UpdateSalesPointPayload>({
          name: opts.name,
          inn: opts.inn,
          type: opts.type,
          maxPopulation: opts.maxPopulation,
          notificationMark: opts.notificationMark,
          municipalEntityId: opts.municipalEntityId,
          locality: this.validateUpdateLocality(opts.locality),
          street: opts.street,
          building: opts.building,
          comment: opts.comment,
          area: opts.area,
          openingHours: opts.openingHours,
          phone: opts.phone ? this.validatePhone(opts.phone) : opts.phone,
          responsiblePersonName: opts.responsiblePersonName,
        }),
      })
    })

    await this.opts.eventDispatcher.processEvents(ctx, [event])
  }

  async delete(ctx: Context, id: string): Promise<void> {
    const event = await runWithROTransaction(this.opts.dbConnection, async tx => {
      if (!(await this.opts.permissionsService.hasPermission(ctx, Permissions.SALES_POINTS_MANAGE))) {
        await this.opts.permissionsService.checkPermission(ctx, Permissions.ME_SALES_POINTS_MANAGE)

        const sp = await this.opts.salesPointsRepository.findById(tx, id)
        await this.checkAccessToMunicipalEntity('<aaea1b69>', ctx, tx, [sp?.municipalEntityId])
      }

      const sp = await this.checkForSalesPointExists('<faadf58e>', ctx, tx, id, { withDeleted: true })

      return await this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.UPDATE,
        entityId: id,
        type: EventType.SALES_POINT_DELETE,
        municipalEntityId: sp.municipalEntityId,
      })
    })

    await this.opts.eventDispatcher.processEvents(ctx, [event])
  }

  private async checkForSalesPointExists(
    errorCode: string,
    ctx: Context,
    tx: ROTransaction,
    id: Id,
    opts?: { withDeleted: boolean },
  ): Promise<SalesPoint> {
    if (opts?.withDeleted)
      return (
        (await this.opts.salesPointsRepository.findById(tx, id)) ??
        throwObjectNotFound(`${errorCode} ${this.translate(ctx, d => d.salesPoints.notFoundByIdError, id)}`)
      )
    else
      return (
        (await this.opts.salesPointsRepository.findByIdNonDeleted(tx, id)) ??
        throwObjectNotFound(`${errorCode} ${this.translate(ctx, d => d.salesPoints.notFoundByIdError, id)}`)
      )
  }

  private async checkAccessToMunicipalEntity(
    errorCode: string,
    ctx: Context,
    tx: ROTransaction,
    meIds: (string | undefined)[],
  ): Promise<void> {
    const filteredMEIds = meIds?.filter(id => id != undefined)
    if (!filteredMEIds.length) {
      throwObjectNotFound(`${errorCode} ${this.translate(ctx, d => d.municipalEntities.notGiven)}`)
    }

    const userID = this.validateUserID(ctx.userId)

    const user =
      (await this.opts.usersRepository.findById(tx, userID)) ??
      throwObjectNotFound(`<04cbb026> ${this.translate(ctx, d => d.users.notFoundByIdError, userID)}`)

    filteredMEIds.forEach(id => {
      if (user.municipalEntityId != id) {
        throwAccessDenied(
          `${errorCode} ${this.opts.translationService.translate(ctx.langCode, d => d.municipalEntities.accessDenied)}`,
        )
      }
    })
  }

  private validateAddress(street: string | undefined, building: string | undefined, comment: string | undefined): void {
    street = street ?? ''
    building = building ?? ''
    comment = comment ?? ''

    if ((street + building + comment).length == 0 || (comment + street).length == 0 || (comment + building).length == 0)
      throwInvalidParameter('<6790ede5> Address or comment must not be empty')
  }
}

function getFieldsToIndex(sp: SalesPoint): (string | undefined)[] {
  return [sp.locality, sp.street, sp.building, sp.comment]
}

export class SalesPointsModelEventProcessor implements ModelEventsProcessor {
  supportedEventTypes: string[] = enumGetValues(EventType)

  constructor(
    private opts: {
      salesPointsRepository: SalesPointsRepository
      eventCreator: ModelEventCreator
      fullTextSearchService: FullTextSearchService
    },
  ) {}

  async createMergeEvent(ctx: Context, tx: ROTransaction, entityId: string): Promise<ModelEvent> {
    const sp =
      (await this.opts.salesPointsRepository.findById(tx, entityId)) ??
      throwObjectNotFound(
        trCore(d => d.salesPoints.notFoundByIdError, entityId),
        { uid: '<d4dd347f>' },
      )
    return await this.opts.eventCreator.create(ctx, tx, {
      entityId,
      mode: CreateEventMode.UPDATE,
      type: EventType.SALES_POINT_MERGE,
      municipalEntityId: sp.municipalEntityId,
      payload: createObj<MergeSalesPointPayload>({
        ...sp,
        createdAt: sp.createdAt.toISOString(),
        deletedAt: sp.deletedAt?.toISOString(),
      }),
    })
  }

  async process(ctx: Context, tx: RWTransaction, event: ModelEvent): Promise<boolean> {
    switch (event.type as EventType) {
      case EventType.SALES_POINT_ADD: {
        const p = event.payload as AddSalesPointPayload
        const sp: SalesPoint = {
          name: p.name,
          inn: p.inn,
          type: p.type,
          maxPopulation: p.maxPopulation,
          notificationMark: p.notificationMark,
          id: event.entityId,
          createdAt: event.createdAt,
          municipalEntityId: p.municipalEntityId,
          locality: p.locality,
          street: p.street,
          building: p.building,
          comment: p.comment,
          area: p.area,
          openingHours: p.openingHours,
          phone: p.phone,
          responsiblePersonName: p.responsiblePersonName,
        }
        await this.opts.salesPointsRepository.save(tx, sp)
        await this.opts.fullTextSearchService.addToIndex(ctx, tx, event.entityId, getFieldsToIndex(sp))
        return true
      }
      case EventType.SALES_POINT_UPDATE: {
        const p = event.payload as UpdateSalesPointPayload
        const sp =
          (await this.opts.salesPointsRepository.findById(tx, event.entityId)) ??
          throwObjectNotFound(
            trCore(d => d.salesPoints.notFoundByIdError, event.entityId),
            { uid: '<b8e72569>' },
          )
        const updatedSp: SalesPoint = {
          ...sp,
          name: p.name ?? sp.name,
          inn: p.inn ?? sp.inn,
          type: p.type ?? sp.type,
          maxPopulation: p.maxPopulation ?? sp.maxPopulation,
          notificationMark: p.notificationMark ?? sp.notificationMark,
          municipalEntityId: p.municipalEntityId ?? sp.municipalEntityId,
          locality: p.locality ?? sp.locality,
          street: p.street ?? sp.street,
          building: p.building ?? sp.building,
          comment: p.comment ?? sp.comment,
          area: p.area ?? sp.area,
          openingHours: p.openingHours ?? sp.openingHours,
          phone: getUpdatedString(p.phone, sp.phone),
          responsiblePersonName: p.responsiblePersonName ?? sp.responsiblePersonName,
        }
        await this.opts.salesPointsRepository.save(tx, updatedSp)
        const fieldsToIndex = getFieldsToIndex(updatedSp)
        if (!isEqual(fieldsToIndex, getFieldsToIndex(sp))) {
          await this.opts.fullTextSearchService.addToIndex(ctx, tx, event.entityId, fieldsToIndex)
        }
        return true
      }
      case EventType.SALES_POINT_DELETE: {
        await this.opts.salesPointsRepository.delete(tx, event.entityId, event.createdAt)
        await this.opts.fullTextSearchService.removeFromIndex(ctx, tx, event.entityId)
        return true
      }
      case EventType.SALES_POINT_MERGE: {
        const p = event.payload as MergeSalesPointPayload
        await this.opts.salesPointsRepository.save(tx, {
          ...p,
          id: event.entityId,
          createdAt: new Date(p.createdAt),
          deletedAt: runIfDefined(p.deletedAt, v => new Date(v)),
        })
        return true
      }
    }
    return false
  }
}
